import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { usePickerAdapter, usePickerTranslations } from '@mui/x-date-pickers/hooks';
import { renderMultiSectionDigitalClockTimeView } from '@mui/x-date-pickers/timeViewRenderers';
import { useLayoutEffect, useRef } from 'react';

type TimeViewProps = Parameters<typeof renderMultiSectionDigitalClockTimeView>[0];
type HourMinuteDigitalClockProps = Pick<
  TimeViewProps,
  | 'disabled'
  | 'maxTime'
  | 'minTime'
  | 'onChange'
  | 'readOnly'
  | 'referenceDate'
  | 'timeSteps'
  | 'value'
>;

const CLOCK_HEIGHT = 232;
const SECTION_WIDTH = 56;

function pad2(value: number) {
  return String(value).padStart(2, '0');
}

function ClockSection({
  ariaLabel,
  items,
  onSelect,
  selected,
}: {
  ariaLabel: string;
  items: { disabled?: boolean; label: string; value: number }[];
  onSelect: (value: number) => void;
  selected: number;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  const didInitialScroll = useRef(false);

  useLayoutEffect(() => {
    if (didInitialScroll.current || !listRef.current) {
      return;
    }
    const selectedItem = listRef.current.querySelector<HTMLElement>('[aria-selected="true"]');
    if (selectedItem) {
      const top =
        selectedItem.offsetTop - listRef.current.clientHeight / 2 + selectedItem.offsetHeight / 2;
      listRef.current.scrollTop = Math.max(0, top);
    }
    didInitialScroll.current = true;
  }, []);

  return (
    <MenuList
      ref={listRef}
      aria-label={ariaLabel}
      autoFocusItem={false}
      dense={false}
      disableListWrap
      disablePadding
      role="listbox"
      sx={{
        maxHeight: CLOCK_HEIGHT,
        overflowAnchor: 'none',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        py: 0.5,
        scrollBehavior: 'auto',
        width: SECTION_WIDTH,
        '&:not(:first-of-type)': {
          borderLeft: 1,
          borderColor: 'divider',
        },
      }}
      variant="menu"
    >
      {items.map(item => (
        <MenuItem
          key={item.value}
          aria-disabled={item.disabled || undefined}
          aria-selected={item.value === selected}
          dense={false}
          disabled={item.disabled}
          disableRipple
          onClick={() => {
            if (!item.disabled) {
              onSelect(item.value);
            }
          }}
          onMouseDown={event => {
            event.preventDefault();
          }}
          role="option"
          selected={item.value === selected}
          sx={{
            justifyContent: 'center',
            minHeight: 40,
            mx: 0.5,
            my: '2px',
            px: 1,
          }}
        >
          {item.label}
        </MenuItem>
      ))}
    </MenuList>
  );
}

/**
 * Side-by-side hour/minute lists that do not re-snap scroll on re-render.
 * MUI's MultiSectionDigitalClock resets scrollTop whenever the selected item's
 * DOM node changes, which makes the columns unusable on Windows.
 */
export function HourMinuteDigitalClock({
  disabled,
  maxTime,
  minTime,
  onChange,
  readOnly,
  referenceDate,
  timeSteps,
  value,
}: HourMinuteDigitalClockProps) {
  const adapter = usePickerAdapter();
  const translations = usePickerTranslations();
  const minuteStep = timeSteps?.minutes && timeSteps.minutes > 0 ? timeSteps.minutes : 1;
  const current = value ?? referenceDate ?? adapter.date();
  const selectedHour = adapter.getHours(current);
  const selectedMinute = adapter.getMinutes(current);

  const isDisabled = (hour: number, minute: number) => {
    if (disabled) {
      return true;
    }
    const candidate = adapter.setMinutes(adapter.setHours(current, hour), minute);
    if (minTime && adapter.isBefore(candidate, minTime)) {
      return true;
    }
    if (maxTime && adapter.isAfter(candidate, maxTime)) {
      return true;
    }
    return false;
  };

  const hours = Array.from({ length: 24 }, (_, hour) => ({
    disabled: isDisabled(hour, selectedMinute),
    label: pad2(hour),
    value: hour,
  }));

  const minutes = Array.from({ length: Math.ceil(60 / minuteStep) }, (_, index) => {
    const minute = index * minuteStep;
    return {
      disabled: isDisabled(selectedHour, minute),
      label: pad2(minute),
      value: minute,
    };
  });

  const emit = (next: typeof current, view: 'hours' | 'minutes') => {
    if (readOnly || disabled || !onChange) {
      return;
    }
    onChange(next, view === 'minutes' ? 'finish' : 'partial', view);
  };

  return (
    <Box
      role="group"
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        width: '100%',
      }}
    >
      <ClockSection
        ariaLabel={translations.selectViewText('hours')}
        items={hours}
        onSelect={hour => emit(adapter.setHours(current, hour), 'hours')}
        selected={selectedHour}
      />
      <ClockSection
        ariaLabel={translations.selectViewText('minutes')}
        items={minutes}
        onSelect={minute => emit(adapter.setMinutes(current, minute), 'minutes')}
        selected={Math.floor(selectedMinute / minuteStep) * minuteStep}
      />
    </Box>
  );
}

export function renderHourMinuteDigitalClock(props: TimeViewProps) {
  return <HourMinuteDigitalClock {...props} />;
}
